import { useEffect, useId, useRef, useState } from 'react';
import { BarChart3, ChevronDown } from 'lucide-react';
import { Button, Checkbox, cn } from '@bill-alps/ui';
import type { DashboardGraphId } from '../lib/graph-selection';

interface GraphOption {
  value: DashboardGraphId;
  label: string;
}

interface DashboardGraphSelectorProps {
  options: readonly GraphOption[];
  value: readonly DashboardGraphId[];
  onChange: (value: DashboardGraphId[]) => void;
  label: string;
  selectionSummary: string;
}

export function DashboardGraphSelector({
  options,
  value,
  onChange,
  label,
  selectionSummary,
}: DashboardGraphSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const idPrefix = useId();
  const selected = new Set(value);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggle = (graphId: DashboardGraphId) => {
    const next = new Set(value);
    if (next.has(graphId)) next.delete(graphId);
    else next.add(graphId);
    onChange([...next]);
  };

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <BarChart3 aria-hidden="true" />
        <span>{label}</span>
        <span className="text-muted-foreground">{selectionSummary}</span>
        <ChevronDown
          aria-hidden="true"
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </Button>

      {open && (
        <div
          role="group"
          aria-label={label}
          className="absolute left-0 z-50 mt-2 min-w-64 space-y-1 rounded-xl border border-border bg-popover p-1.5 shadow-hard-lg"
        >
          {options.map((option) => {
            const inputId = `${idPrefix}-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={inputId}
                className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium hover:bg-muted"
              >
                <Checkbox
                  id={inputId}
                  checked={selected.has(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
